import { notification } from 'antd'
import { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getAdminErrorMessage } from '../adminErrorModel'
import {
  cancelLongRunningTask,
  createJobRefreshTask,
  CreateTaskResult,
  getLongRunningTask,
  listLongRunningTasks,
  LongRunningTaskDetails,
  LongRunningTaskSummary,
  resumeLongRunningTask,
  ResumeTaskResult,
} from './longRunningTasksApi'
import { StatusFilter, toStatusParam } from './longRunningTasksModel'
import { CreateTaskRequest, toJobRefreshParameters } from './taskTypesModel'

const BASE_KEY = ['admin', 'long-running-tasks'] as const
const LIST_KEY = [...BASE_KEY, 'list'] as const
const DETAIL_KEY = [...BASE_KEY, 'detail'] as const

export type RefetchInterval<T> = number | false | ((data: T | undefined) => number | false)

export const useLongRunningTasksQuery = (
  filter: StatusFilter,
  refetchInterval: RefetchInterval<LongRunningTaskSummary[]> = false,
) => {
  return useQuery({
    queryKey: [...LIST_KEY, filter],
    queryFn: () => listLongRunningTasks(toStatusParam(filter)),
    keepPreviousData: true,
    retry: false,
    refetchInterval,
    onError: (error: unknown) => {
      notification.error({ message: getAdminErrorMessage(error, 'Failed to load long-running tasks') })
    },
  })
}

export const useLongRunningTaskQuery = (
  id: number | null,
  refetchInterval: RefetchInterval<LongRunningTaskDetails> = false,
) => {
  return useQuery({
    queryKey: [...DETAIL_KEY, id],
    queryFn: () => getLongRunningTask(id as number),
    enabled: id !== null,
    retry: false,
    refetchInterval,
  })
}

const useTaskInvalidation = () => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries(BASE_KEY)
  }
}

export const useCancelTaskMutation = () => {
  const invalidate = useTaskInvalidation()
  return useMutation<void, AxiosError, number>({
    mutationFn: (id: number) => cancelLongRunningTask(id),
    onSuccess: () => {
      invalidate()
      notification.success({ message: 'Cancel requested' })
    },
    onError: error => {
      notification.error({ message: getAdminErrorMessage(error, 'Failed to cancel the task') })
    },
  })
}

export const useResumeTaskMutation = () => {
  const invalidate = useTaskInvalidation()
  return useMutation<ResumeTaskResult, AxiosError, number>({
    mutationFn: (id: number) => resumeLongRunningTask(id),
    onSuccess: () => {
      invalidate()
      notification.success({ message: 'Resume requested' })
    },
    onError: error => {
      notification.error({ message: getAdminErrorMessage(error, 'Failed to resume the task') })
    },
  })
}

const createTask = (request: CreateTaskRequest): Promise<CreateTaskResult> => {
  switch (request.type) {
    case 'job-refresh':
      return createJobRefreshTask(toJobRefreshParameters(request.values))
  }
}

export const useCreateTaskMutation = () => {
  const invalidate = useTaskInvalidation()
  return useMutation<CreateTaskResult, AxiosError, CreateTaskRequest>({
    mutationFn: createTask,
    onSuccess: () => {
      invalidate()
      notification.success({ message: 'Task created' })
    },
  })
}
